import { t } from "@/i18n";
import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Link2, Share2, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";

export const shareUrl = (token) => `${window.location.origin}/share/${token}`;
export const galleryUrl = (token) => `${window.location.origin}/gallery/${token}`;

export const ShareLinkDialog = ({ open, onClose, title, description, token, url, onToggle, prefix = "share", note }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggle = async (on) => {
    setBusy(true);
    try {
      await onToggle(on);
      if (!on) setCopied(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied!");
    } catch {
      toast.error("Couldn't copy — select the link and copy it manually.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={`${prefix}-dialog`}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Share2 className="w-6 h-6 text-[#FF5252]" /> {title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between rounded-2xl border-2 border-slate-200 px-4 py-3">
          <span className="font-bold">Sharing is {token ? "on" : "off"}</span>
          <Switch checked={!!token} onCheckedChange={toggle} disabled={busy} data-testid={`${prefix}-toggle`} />
        </div>
        {token && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-2xl bg-[#FFF9F2] border-2 border-[#FFD166] px-3 py-2">
              <Link2 className="w-4 h-4 text-slate-500 shrink-0" />
              <input readOnly value={url} onFocus={(e) => e.target.select()} data-testid={`${prefix}-link-input`}
                className="flex-1 bg-transparent text-sm font-bold outline-none min-w-0" />
              <button onClick={copy} className="btn-pop btn-ink !py-2 !px-3 text-sm shrink-0" data-testid={`${prefix}-copy-button`}>
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-1.5"><ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" /> {note}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const ShareDialog = ({ art, onClose, onChange }) => (
  <ShareLinkDialog open={!!art} onClose={onClose} prefix="share"
    title={`Share "${art?.page_title}"`}
    description={t("send_a_link_to_grandparents_and_family_a")}
    token={art?.share_token} url={art ? shareUrl(art.share_token) : ""}
    note="Child-safe: the page shows only the picture, its title and a first name — no email, prompts or account details. Turn sharing off any time to disable the link."
    onToggle={async (on) => {
      if (on) {
        const { data } = await api.post(`/artworks/${art.id}/share`);
        onChange({ ...art, share_token: data.share_token });
      } else {
        await api.delete(`/artworks/${art.id}/share`);
        onChange({ ...art, share_token: null });
      }
    }} />
);

export const GalleryShareDialog = ({ open, onClose, token, onChange }) => (
  <ShareLinkDialog open={open} onClose={onClose} prefix="gallery-share"
    title={t("share_my_whole_gallery")}
    description={t("one_link_to_every_coloured_picture_new_o")}
    token={token} url={galleryUrl(token)}
    note="Child-safe: the gallery shows only pictures, titles and a first name — no email, prompts or account details. Turn sharing off any time to disable the link."
    onToggle={async (on) => {
      if (on) {
        const { data } = await api.post("/gallery/share");
        onChange(data.gallery_token);
      } else {
        await api.delete("/gallery/share");
        onChange(null);
      }
    }} />
);
